<template>
  <!-- Backup codes can't be re-fetched, so the codes step ignores esc/overlay; Done/x-close still work -->
  <Modal ref="modalRef" width="27.5rem" :dismissable="step !== 'codes'" @after-close="reset">
    <template #header>
      <View v-if="step === 'codes'" align="center" gap="sm">
        <Icon name="check-circle" class="success-icon" />
        <Text variant="h4">Two-factor is on</Text>
      </View>
      <Text v-else variant="h4">Set up two-factor</Text>
    </template>

    <template #body>
      <View v-if="step === 'scan'" direction="column" gap="md">
        <Text variant="body-medium" color="secondary">
          Scan with your authenticator app, then enter the 6-digit code it shows.
        </Text>

        <View v-if="isLoadingSetup" justify="center" padding="lg">
          <LoadingIndicator />
        </View>

        <template v-else-if="setup">
          <View align="center" gap="md">
            <img :src="setup.qrCodeDataUrl" alt="Two-factor QR code" class="qr-code" />
            <View direction="column" gap="xs" class="manual-key-block">
              <Text variant="body-small" color="tertiary">Can't scan? Enter this key manually:</Text>
              <Text variant="label-medium" class="manual-key">{{ setup.manualKey }}</Text>
            </View>
          </View>

          <CodeInput ref="codeInputRef" v-model="code" compact @complete="enable" />
          <Text v-if="codeError" variant="body-small" class="code-error">{{ codeError }}</Text>
        </template>
      </View>

      <View v-else direction="column" gap="md">
        <Text variant="body-medium" color="secondary">
          Save these backup codes somewhere safe — each works once if you lose your phone.
        </Text>
        <div class="backup-codes">
          <span v-for="backupCode in backupCodes" :key="backupCode" class="backup-code">{{ backupCode }}</span>
        </div>
      </View>
    </template>

    <template #footer>
      <template v-if="step === 'scan'">
        <Button variant="ghost" size="medium" @click="close">Cancel</Button>
        <Button size="medium" :disabled="code.length < 6 || !setup" :loading="isEnabling" @click="enable">
          Turn on
        </Button>
      </template>
      <template v-else>
        <Button variant="outline" size="medium" @click="downloadCodes">
          <Icon name="file-download-02" size="small" />
          Download codes
        </Button>
        <Button size="medium" @click="finish">Done</Button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';

import {
  Button,
  CodeInput,
  extractErrorMessage,
  Icon,
  LoadingIndicator,
  Modal,
  Text,
  useAlertsStore,
  View,
} from '@tallo/design-system';
import { profileService, TwoFactorSetupDto, useInvestorStore } from '@tallo/investor/investor';
import { downloadFile } from '@tallo/utility';

const emit = defineEmits<{ enabled: [] }>();

const investorStore = useInvestorStore();
const alertsStore = useAlertsStore();

const modalRef = ref<InstanceType<typeof Modal>>();
const codeInputRef = ref<InstanceType<typeof CodeInput>>();
const step = ref<'scan' | 'codes'>('scan');
const setup = ref<TwoFactorSetupDto | null>(null);
const backupCodes = ref<string[]>([]);
const code = ref('');
const codeError = ref('');
const isLoadingSetup = ref(false);
const isEnabling = ref(false);

async function show() {
  step.value = 'scan';
  modalRef.value?.show();
  isLoadingSetup.value = true;

  try {
    setup.value = await profileService.setupTwoFactor();
    await nextTick();
    codeInputRef.value?.focus();
  } catch (error) {
    alertsStore.error(extractErrorMessage(error, 'Could not start the two-factor setup'));
    modalRef.value?.close();
  } finally {
    isLoadingSetup.value = false;
  }
}

function close() {
  modalRef.value?.close();
}

function reset() {
  // Backup codes exist only in this modal - once it closes they are gone for good
  step.value = 'scan';
  setup.value = null;
  backupCodes.value = [];
  code.value = '';
  codeError.value = '';
}

async function enable() {
  if (code.value.length < 6 || isEnabling.value || !setup.value) return;

  isEnabling.value = true;
  codeError.value = '';

  try {
    backupCodes.value = await profileService.enableTwoFactor(code.value);
  } catch (error) {
    codeError.value = extractErrorMessage(error, 'Invalid code. Please try again.');
    code.value = '';
    codeInputRef.value?.focus();
    isEnabling.value = false;
    return;
  }

  // 2FA is on and the codes are shown; a store-refresh failure here must not read as a bad code
  step.value = 'codes';
  isEnabling.value = false;
  await investorStore.load().catch(() => undefined);
  emit('enabled');
}

function downloadCodes() {
  const content = `Tallo two-factor backup codes\n\n${backupCodes.value.join('\n')}\n\nEach code works once.\n`;
  downloadFile(new Blob([content], { type: 'text/plain' }), 'tallo-backup-codes.txt');
}

function finish() {
  alertsStore.success('Two-factor authentication is on');
  close();
}

defineExpose({ show });
</script>

<style scoped lang="scss">
.success-icon {
  color: var(--t-color-green-500);
}

.qr-code {
  width: 8.75rem;
  height: 8.75rem;
  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-md);
  flex-shrink: 0;
}

.manual-key-block {
  min-width: 0;
}

.manual-key {
  font-family: ui-monospace, Menlo, monospace;
  word-break: break-all;
}

.code-error {
  color: var(--t-text-color-danger-primary);
}

.backup-codes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--t-spacing-sm) var(--t-spacing-lg);
  padding: var(--t-spacing-md);
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-neutral-50);
}

.backup-code {
  font:
    var(--t-font-weight-medium) 0.875rem/1.5rem ui-monospace,
    Menlo,
    monospace;
  color: var(--t-text-color-primary);
}
</style>
